import { jsPDF } from 'jspdf';
import JsBarcode from 'jsbarcode';

/**
 * Renders a Brother DK-2251 sized (2.4" x 1.5") S/N label PDF for a
 * PurchasedPC and triggers a browser download.
 *
 *   ┌────────────────────────────────────────┐
 *   │▐ LANFORGE                       SERIAL │
 *   │  ────────────────────────────────────  │
 *   │            LAN-PC-228992               │
 *   │  █▌██▌█▌█▌█▌█▌██▌█▌█▌█▌█▌█▌█▌██   │
 *   │  ────────────────────────────────────  │
 *   │  MODEL   LANForge 9800X3D // 5070…    │
 *   │  ORDER   LAN-559526                    │
 *   │                          BUILT · USA   │
 *   └────────────────────────────────────────┘
 */
export interface PcLabelInput {
  productName: string;
  orderNumber?: string;
  serialNumber: string;
  builtAt?: Date;
}

// Points is jsPDF's native unit for text sizing — keeping everything in one
// unit avoids the "inches for coords / points for text / who-knows for
// setCharSpace" trap that the previous version fell into.
const PT_PER_INCH = 72;
const LABEL_W = 2.4 * PT_PER_INCH; // 172.8pt
const LABEL_H = 1.5 * PT_PER_INCH; // 108pt

// LANForge accent green — appears as a thin left-edge stripe so the label
// reads as company-branded without needing a raster logo.
const ACCENT_RGB: [number, number, number] = [0, 200, 130];
const RULE_GREY = 220;
const LABEL_GREY = 140;
const CAPTION_GREY = 170;

const barcodeDataUrl = (value: string): string => {
  const canvas = document.createElement('canvas');
  JsBarcode(canvas, value, {
    format: 'CODE128',
    width: 2,
    height: 60,
    displayValue: false,
    margin: 0,
    background: '#ffffff',
    lineColor: '#000000',
  });
  return canvas.toDataURL('image/png');
};

// Trim a string so it fits `maxWidthPt` in the current font. jsPDF's
// getTextWidth returns whatever unit the document was constructed with — pt
// here — so this stays honest across font/size changes.
const fitText = (doc: jsPDF, s: string, maxWidthPt: number): string => {
  if (!s) return '';
  if (doc.getTextWidth(s) <= maxWidthPt) return s;
  let clipped = s;
  while (clipped.length > 1 && doc.getTextWidth(clipped + '…') > maxWidthPt) {
    clipped = clipped.slice(0, -1);
  }
  return clipped.trimEnd() + '…';
};

const formatDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const generatePcLabelPdf = (input: PcLabelInput): Blob => {
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: [LABEL_H, LABEL_W],
  });

  const padX = 10;
  const stripeW = 4;
  const contentLeft = padX + 2; // slight breathing room from the accent
  const contentRight = LABEL_W - padX;

  // ── Accent stripe ────────────────────────────────────────────────────────
  doc.setFillColor(...ACCENT_RGB);
  doc.rect(0, 0, stripeW, LABEL_H, 'F');

  // ── Header row ───────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(20);
  doc.text('LANForge', contentLeft, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5.5);
  doc.setTextColor(LABEL_GREY);
  doc.text('SERIAL', contentRight, 13, { align: 'right' });

  // ── Divider under header ─────────────────────────────────────────────────
  doc.setDrawColor(RULE_GREY);
  doc.setLineWidth(0.4);
  doc.line(contentLeft, 17, contentRight, 17);

  // ── Serial number (dominant text) ────────────────────────────────────────
  doc.setFont('courier', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(0);
  doc.text(input.serialNumber, LABEL_W / 2, 33, { align: 'center' });

  // ── Barcode ──────────────────────────────────────────────────────────────
  const barcodeUrl = barcodeDataUrl(input.serialNumber);
  const barcodeX = contentLeft + 4;
  const barcodeY = 38;
  const barcodeW = contentRight - contentLeft - 8;
  const barcodeH = 26;
  doc.addImage(barcodeUrl, 'PNG', barcodeX, barcodeY, barcodeW, barcodeH);

  // ── Divider above metadata ───────────────────────────────────────────────
  doc.setDrawColor(RULE_GREY);
  doc.line(contentLeft, 70, contentRight, 70);

  // ── Metadata table ───────────────────────────────────────────────────────
  const labelX = contentLeft;
  const valueX = contentLeft + 34;
  const valueMaxWidth = contentRight - valueX;

  // Labels
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.setTextColor(LABEL_GREY);
  doc.text('MODEL', labelX, 82);
  doc.text('ORDER', labelX, 94);

  // Values
  doc.setTextColor(20);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text(fitText(doc, input.productName || 'PC', valueMaxWidth), valueX, 82);

  doc.setFont('courier', 'normal');
  doc.setFontSize(8);
  doc.text(fitText(doc, input.orderNumber || '—', valueMaxWidth), valueX, 94);

  // ── Bottom-right built stamp ─────────────────────────────────────────────
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5);
  doc.setTextColor(CAPTION_GREY);
  doc.text(
    `BUILT ${formatDate(input.builtAt || new Date())} · USA`,
    contentRight,
    104,
    { align: 'right' }
  );

  return doc.output('blob');
};

export const downloadPcLabelPdf = (input: PcLabelInput) => {
  const blob = generatePcLabelPdf(input);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sn-tag-${input.serialNumber}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
